import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  FormHelperText,
  Autocomplete,
  TextField,
  CircularProgress,
  Stack,
} from '@mui/material'

import { NetworkDesmosAct } from '@/core/models/network.model'
import { useGetActsQuery, useLazyGetActsQuery } from '@/core/services/acts'
import { Trans } from '@lingui/macro'

interface WaitingRoomOption {
  _id: string
  name: string
}

interface EstablishmentOption {
  _id: string
  name: string
}

interface ActOption {
  _id: string
  libelle: string
  honoraire: string
  id_transposition: string
}

interface DesmosActFormDialogProps {
  open: boolean
  onClose: () => void
  onSave: (act: NetworkDesmosAct) => void
  initialAct?: NetworkDesmosAct | null
  waitingRooms?: WaitingRoomOption[]
  establishments?: EstablishmentOption[]
}

export const DesmosActFormDialog: React.FC<DesmosActFormDialogProps> = ({ open, onClose, onSave, initialAct, waitingRooms = [], establishments = [] }) => {
  const { t } = useTranslation()

  // Fetch acts from admin portal
  const { data: acts = [], isLoading: isLoadingActs } = useGetActsQuery({})
  const [getActs, { isLoading: isSearching }] = useLazyGetActsQuery()

  const [formData, setFormData] = useState<NetworkDesmosAct>({
    actId: '',
    autoCreate: false,
    waitingRooms: [],
    establishmentId: '',
  })

  const [selectedAct, setSelectedAct] = useState<ActOption | null>(null)
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    if (initialAct) {
      setFormData(initialAct)
      // Find the act details if we have an actId
      if (initialAct.actId && acts.length > 0) {
        const act = acts.find((a: any) => a._id === initialAct.actId)
        if (act) {
          setSelectedAct({
            _id: act._id,
            libelle: act.libelle,
            honoraire: act.honoraire,
            id_transposition: act.id_transposition,
          })
        }
      }
    } else {
      setFormData({
        actId: '',
        autoCreate: false,
        waitingRooms: [],
        establishmentId: '',
      })
      setSelectedAct(null)
      setInputValue('')
    }
  }, [initialAct, open, acts])

  const handleActChange = (_event: any, newValue: ActOption | null) => {
    setSelectedAct(newValue)
    setFormData((prev) => ({
      ...prev,
      actId: newValue?._id || '',
    }))
  }

  const handleInputChange = (_event: any, newInputValue: string) => {
    setInputValue(newInputValue)
    // Trigger search if input has changed
    if (newInputValue && newInputValue.length > 0) {
      getActs({ smartSearch: newInputValue })
    }
  }

  const handleWaitingRoomsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value
    setFormData((prev) => ({
      ...prev,
      waitingRooms: typeof value === 'string' ? value.split(',') : value,
    }))
  }

  const handleEstablishmentChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      establishmentId: event.target.value,
    }))
  }

  const isFormValid = formData.actId && formData.establishmentId

  const handleSubmit = () => {
    if (!isFormValid) return
    onSave(formData)
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialAct ? t('common:button.edit.label') + ' Desmos Act' : t('page.network.desmosConfig.addAct')}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} pt={2} pb={4}>
          {/* Establishment Selector - Required */}
          <FormControl fullWidth required error={!formData.establishmentId}>
            <InputLabel id="establishment-label">{t('field.establishment.label', { defaultValue: 'Establishment' })}</InputLabel>
            <Select
              labelId="establishment-label"
              value={formData.establishmentId || ''}
              onChange={handleEstablishmentChange}
              input={<OutlinedInput label={t('field.establishment.label', { defaultValue: 'Establishment' })} />}
            >
              {establishments.map((est) => (
                <MenuItem key={est._id} value={est._id}>
                  {est.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {t('page.network.desmosConfig.establishmentHelper', { defaultValue: 'Select the establishment for this Desmos act. Each establishment has its own Desmos installation.' })}
            </FormHelperText>
          </FormControl>

          {/* Act Selector */}
          <FormControl>
            <Autocomplete
              value={selectedAct}
              onChange={handleActChange}
              inputValue={inputValue}
              onInputChange={handleInputChange}
              options={acts}
              getOptionLabel={(option: any) => `${option.libelle} (${option.id_transposition}) - ${option.honoraire}€`}
              loading={isLoadingActs || isSearching}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('field.desmosAct.selectAct.label')}
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingActs || isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option: any) => (
                <li {...props}>
                  <Box>
                    <strong>{option.libelle}</strong>
                    <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                      {option.id_transposition} - {option.honoraire}€
                    </Box>
                  </Box>
                </li>
              )}
            />
            <FormHelperText sx={{ position: 'relative' }}>
              {t('page.network.desmosConfig.selectActHelper', {
                defaultValue: 'Search and select an act from the admin portal. Acts are managed centrally for consistency.',
              })}
            </FormHelperText>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                name="autoCreate"
                checked={formData.autoCreate}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    autoCreate: e.target.checked,
                  }))
                }
              />
            }
            label={t('page.network.desmosConfig.autoCreateLabel', { defaultValue: 'Auto-create this act for consultations' })}
          />
          <FormControl fullWidth>
            <InputLabel id="waiting-rooms-label">{t('field.waitingRooms.label', { defaultValue: 'Waiting Rooms' })}</InputLabel>
            <Select
              labelId="waiting-rooms-label"
              multiple
              value={formData.waitingRooms || []}
              onChange={handleWaitingRoomsChange}
              input={<OutlinedInput label={t('field.waitingRooms.label', { defaultValue: 'Waiting Rooms' })} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const room = waitingRooms.find((wr) => wr._id === value)
                    return <Chip key={value} label={room?.name || value} size="small" />
                  })}
                </Box>
              )}
            >
              {waitingRooms.map((room) => (
                <MenuItem key={room._id} value={room._id}>
                  {room.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {t('page.network.desmosConfig.waitingRoomsHelper', { defaultValue: 'Leave empty to apply to all waiting rooms in the network' })}
            </FormHelperText>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="neutral">
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!isFormValid}>
          <Trans>Save</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
